/**
 * useWhatsAppSend — shared hook powering the "Also send WhatsApp" affordance
 * across the Emails, Reminders, and Acknowledgments tabs of a Drawdown.
 *
 * The hook owns:
 *   • Preflight state (eligible, reason, message, all published templates)
 *   • User intent (enabled flag, picked templateId)
 *   • Active WhatsApp job + result-modal toggle
 *   • A `triggerSend` fn that posts to /api/whatsapp/drawdowns/:id/send and
 *     polls the resulting job until terminal.
 *
 * The same Drawdown WhatsApp send endpoint serves all three contexts — only
 * the chosen template + recipient list differ. The preflight enumerates every
 * published drawdown-category template (send-ready or not) so admins can see
 * the full menu and any per-template warnings inline.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TERMINAL_STATES = ['completed', 'aborted', 'failed'];

export default function useWhatsAppSend(drawdownId, section) {
  const [preflight, setPreflight] = useState({
    eligible: false,
    reason: 'checking',
    message: 'Checking WhatsApp eligibility…',
    templates: [],
    all_published_templates: [],
  });
  const [enabled, setEnabled] = useState(false);
  const [templateId, setTemplateId] = useState('');
  const [activeJob, setActiveJob] = useState(null);
  const [resultOpen, setResultOpen] = useState(false);

  const loadPreflight = useCallback(async () => {
    if (!drawdownId) return;
    try {
      // When the caller supplies a section (emails / reminders /
      // acknowledgments), only templates tagged for that section will be
      // enumerated by preflight. Legacy templates without any section tag
      // are hidden — matches the opt-in gating policy.
      const url = section
        ? `${API}/whatsapp/drawdowns/${drawdownId}/preflight?section=${encodeURIComponent(section)}`
        : `${API}/whatsapp/drawdowns/${drawdownId}/preflight`;
      const { data } = await axios.get(url);
      const eligible = data?.eligible ?? data?.ok ?? false;
      const reason = data?.reason || (eligible ? 'ok' : 'unknown');
      const message = data?.message || (eligible ? 'Ready to send' : 'Not eligible');
      const next = {
        eligible,
        reason,
        message,
        templates: data?.templates || [],
        all_published_templates: data?.all_published_templates || data?.templates || [],
      };
      setPreflight(next);
      // Default to the first send-ready template only — admin can still pick
      // an unmapped one, the UI will surface that it can't be sent until fixed.
      setTemplateId((prev) => {
        if (prev) return prev;
        const firstReady = (next.templates || [])[0];
        return firstReady ? firstReady.id : '';
      });
      if (!eligible) setEnabled(false);
    } catch (err) {
      setPreflight({
        eligible: false,
        reason: 'preflight_failed',
        message: err.response?.data?.detail || 'Failed to check WhatsApp readiness',
        templates: [],
        all_published_templates: [],
      });
      setEnabled(false);
    }
  }, [drawdownId, section]);

  useEffect(() => { loadPreflight(); }, [loadPreflight]);

  // Poll active job until terminal
  useEffect(() => {
    if (!activeJob || TERMINAL_STATES.includes(activeJob.status)) return;
    const tick = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/whatsapp/jobs/${activeJob.id}`);
        setActiveJob(data);
      } catch { /* swallow — UI will retry */ }
    }, 1500);
    return () => clearInterval(tick);
  }, [activeJob?.id, activeJob?.status]);

  /**
   * Fire a WhatsApp send for the supplied investor IDs.
   * Returns { ok: true, jobId } or { ok: false, error } so callers can chain.
   */
  const triggerSend = useCallback(async ({
    drawdownInvestorIds,
    preferProtected = true,
    dateFormat = 'dd mmmm yyyy',
    numberFormat = 'western',
  }) => {
    if (!enabled || !preflight.eligible || !templateId) {
      return { ok: false, error: 'WhatsApp send is not configured' };
    }
    if (!drawdownInvestorIds || drawdownInvestorIds.length === 0) {
      return { ok: false, error: 'No recipients selected' };
    }
    try {
      const { data } = await axios.post(`${API}/whatsapp/drawdowns/${drawdownId}/send`, {
        template_id: templateId,
        drawdown_investor_ids: drawdownInvestorIds,
        prefer_protected: preferProtected,
        date_format: dateFormat,
        number_format: numberFormat,
      });
      const { data: job } = await axios.get(`${API}/whatsapp/jobs/${data.job_id}`);
      setActiveJob(job);
      setResultOpen(true);
      return { ok: true, jobId: data.job_id };
    } catch (err) {
      return { ok: false, error: err.response?.data?.detail || err.message };
    }
  }, [drawdownId, enabled, preflight.eligible, templateId]);

  // The picked template's send-ready signal. If the admin has explicitly
  // chosen an unmapped template, we surface that so they can't accidentally
  // send something that would arrive as `[unmapped]`.
  const pickedTemplate = (preflight.all_published_templates || []).find(t => t.id === templateId);
  const pickedNotReadyReason = (pickedTemplate && pickedTemplate.is_send_ready === false)
    ? (pickedTemplate.not_ready_reason || 'Selected template has unmapped variables')
    : '';

  return {
    preflight,
    enabled,
    setEnabled,
    templateId,
    setTemplateId,
    activeJob,
    resultOpen,
    setResultOpen,
    triggerSend,
    reloadPreflight: loadPreflight,
    pickedTemplate,
    pickedNotReadyReason,
    // canSend = admin intends to send AND the config is up AND the picked template can carry payload
    canSend: !!(enabled && preflight.eligible && templateId && !pickedNotReadyReason),
  };
}
